// SPDX-License-Identifier: MIT
/**
 * poster-lockup (closing, 5–7 beats). A flat, printed close with no particles and no glow: a hairline
 * rule draws across the frame, the mark rises out of it and lands on the downbeat, the name follows
 * glyph by glyph, a brand-colour bar runs under the lockup, and the tagline and URL drop out of the
 * rule below. Everything enters through the one line, so the frame reads as a composed poster.
 *
 * Same props and logo handling as `ember-lockup`, so the two swap freely: a symbol gets the name set
 * beside it, a logo that contains the name is used alone, and without a logo the name is set large.
 */
import * as z from "zod/mini";
import { font, rgba } from '../brand.ts';
import * as kit from '../kit/index.ts';
import type { Box } from '../kit/path.ts';
import { DESIGN_H, DESIGN_W, defineScene } from '../scene.ts';
import { displayTracking, fitLine, logoHasWordmark, markBox, sceneBeats } from './lib.ts';
const LEFT = 190;
const RIGHT = DESIGN_W - 190;
const RULE_Y = 610;
/** Gap between the lockup's baseline and the rule, and between the mark and the name. */
const LIFT = 44;
const GAP = 40;
const TAG_PX = 50;
const TAG_LH = 64;
/** Baseline of the first tagline line and of the URL, below the rule. */
const BELOW = RULE_Y + 92;
/** The beat the mark lands on. */
const LAND = 2;
export default defineScene({
    id: 'poster-lockup',
    version: '1.0.0',
    aspects: ['16:9'],
    propsSchema: z.object({
        /** Name set next to a symbol-only logo (default: the brand name). */
        wordmark: z.optional(z.string().check(z.maxLength(24))),
        tagline: z.string().check(z.maxLength(60)),
        url: z.optional(z.string().check(z.maxLength(40))),
    }),
    glyphs: (p) => (p.wordmark ?? '') + p.tagline + (p.url ?? ''),
    motion: (_p, env) => (env.localBeat < LAND + 0.6 ? 0.6 : 0.1),
    audioEvents: (p) => [
        { beat: LAND, sfx: 'tick' as const, gain: 0.8 },
        ...(p.url ? [{ beat: LAND + 1, sfx: 'blip' as const, gain: 0.35 }] : []),
    ],
    // Print, not light: the style's lens is turned down so hairlines and small type stay clean.
    post: (_p, env) => (env.style.background === 'dark' ? { bloom: 0.5, lens: 0.5 } : { bloom: 0.3, lens: 0 }),
    draw(ctx, props, env) {
        const th = kit.theme(env);
        const brand = env.brand;
        const logo = brand.logo;
        const L = sceneBeats(env);
        ctx.fillStyle = th.bg;
        ctx.fillRect(0, 0, DESIGN_W, DESIGN_H);
        if (th.dark)
            kit.fx.pool(ctx, LEFT + 500, RULE_Y - 120, 1100, th.primary, 0.05);
        const mode = !logo ? 'text' : logoHasWordmark(brand) ? 'logo' : 'lockup';
        const word = mode === 'logo' ? '' : (props.wordmark ?? brand.name);
        const base = RULE_Y - LIFT;
        // --- Geometry: everything sits on the baseline above the rule, flush left -------------------
        const weight = mode === 'text' ? 800 : 700;
        let wordPx = 0;
        let markB: Box = { x: 0, y: 0, w: 0, h: 0 };
        if (mode === 'lockup') {
            wordPx = fitLine(ctx, brand, word, weight, 170, 1000);
            markB = markBox(brand, 0, 0, wordPx * 2.4, wordPx * 1.05);
        }
        else if (mode === 'text') {
            wordPx = fitLine(ctx, brand, word, weight, 230, RIGHT - LEFT);
        }
        else {
            markB = markBox(brand, 0, 0, 1100, 250);
        }
        if (logo && mode !== 'text')
            markB = { ...markB, x: LEFT, y: base - markB.h };
        const wordX = mode === 'lockup' ? LEFT + markB.w + GAP : LEFT;
        let ww = 0;
        if (word) {
            ctx.font = font(brand, weight, wordPx);
            ctx.letterSpacing = displayTracking(word, wordPx);
            ww = ctx.measureText(word).width;
            ctx.letterSpacing = '0px';
        }
        const lockupW = mode === 'logo' ? markB.w : wordX + ww - LEFT;
        kit.camera.zoom(ctx, 1 + 0.018 * kit.beat.seg(env, 0, L, kit.curves.silk), DESIGN_W / 2, RULE_Y);
        // --- The rule: draws across, and takes the hit when the mark lands --------------------------
        const draw = kit.beat.seg(env, 0, 0.9, kit.curves.glide);
        const hit = kit.beat.hits(env, [LAND], 8);
        ctx.fillStyle = rgba(th.fg, th.dark ? 0.32 : 0.4);
        const ruleH = 2 + 3 * hit;
        ctx.fillRect(LEFT, RULE_Y - ruleH / 2, (RIGHT - LEFT) * draw, ruleH);
        const bar = kit.beat.seg(env, LAND, LAND + 0.6, kit.curves.glide);
        if (bar > 0) {
            ctx.fillStyle = th.primary;
            ctx.fillRect(LEFT, RULE_Y - 3, lockupW * bar, 6);
        }
        // Rising out of the rule: masked above it, travelling up from below it.
        const above = (c: CanvasRenderingContext2D) => {
            c.beginPath();
            c.rect(0, 0, DESIGN_W, RULE_Y - 3);
            c.clip();
        };
        const below = (c: CanvasRenderingContext2D) => {
            c.beginPath();
            c.rect(0, RULE_Y + 3, DESIGN_W, DESIGN_H - RULE_Y);
            c.clip();
        };
        // --- The mark lands on the downbeat --------------------------------------------------------------
        if (logo && mode !== 'text') {
            const k = kit.beat.seg(env, LAND - 0.9, LAND, kit.curves.snap);
            if (k > 0)
                env.crisp((c) => {
                    above(c);
                    c.translate(0, (1 - k) * (markB.h + LIFT + 20));
                    for (const part of logo.parts) {
                        c.fillStyle = part.fill ?? th.primary;
                        kit.path.tracePath(c, kit.path.fit(part.subpaths, markB, logo.bounds));
                        c.fill(part.fillRule);
                    }
                });
            env.report?.element('logo', [markB.x, markB.y, markB.w, markB.h]);
        }
        else
            env.report?.element('logo', [LEFT, base - wordPx * 0.75, ww, wordPx * 0.75]);
        // --- The name follows, glyph by glyph ----------------------------------------------------------
        if (word) {
            const t0 = mode === 'text' ? LAND - 0.9 : LAND - 0.65;
            env.crisp((c) => {
                above(c);
                c.font = font(brand, weight, wordPx);
                c.letterSpacing = displayTracking(word, wordPx);
                c.fillStyle = th.fg;
                c.textBaseline = 'alphabetic';
                kit.text.glyphs(c, word, wordX, base, 'left', (i, n) => {
                    const s = t0 + kit.beat.stagger(i, n, 0.45);
                    const k = kit.beat.seg(env, s, s + 0.7, kit.curves.snap);
                    return { dy: (1 - k) * (wordPx + LIFT) };
                });
                c.letterSpacing = '0px';
            });
        }
        // --- Tagline and URL drop out of the rule ----------------------------------------------------------
        const urlW = props.url ? measureUrl(ctx, brand, props.url) : 0;
        env.crisp((c) => {
            below(c);
            c.textBaseline = 'alphabetic';
            c.font = font(brand, 500, TAG_PX);
            const lines = kit.text.wrap(c, props.tagline, Math.min(1100, RIGHT - LEFT - urlW - 120)).slice(0, 2);
            c.fillStyle = rgba(th.fg, 0.86);
            lines.forEach((line, i) => {
                const k = kit.beat.seg(env, LAND + 0.35 + i * 0.18, LAND + 1.05 + i * 0.18, kit.curves.glide);
                if (k <= 0)
                    return;
                c.fillText(line.text, LEFT, BELOW + i * TAG_LH - (1 - k) * (TAG_LH + 40));
            });
            if (props.url) {
                const k = kit.beat.seg(env, LAND + 0.9, LAND + 1.5, kit.curves.glide);
                if (k > 0) {
                    c.font = font(brand, 500, 28, 'mono');
                    c.letterSpacing = '4px';
                    c.textAlign = 'right';
                    c.fillStyle = th.accentText;
                    c.fillText(props.url, RIGHT, BELOW - (1 - k) * 70);
                    c.letterSpacing = '0px';
                }
            }
        });
    },
});
function measureUrl(ctx: CanvasRenderingContext2D, brand: Parameters<typeof font>[0], url: string): number {
    ctx.save();
    ctx.font = font(brand, 500, 28, 'mono');
    ctx.letterSpacing = '4px';
    const w = ctx.measureText(url).width;
    ctx.restore();
    return w;
}
